import Post from '../../components/Post';
import Input from './Input';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AgendaPost, 
        GetAgendaUserQuery, 
        GetAgendaUserQueryVariables, 
        ModelSortDirection,
        PostByRoomQuery,
        PostByRoomQueryVariables,
        PostsByCompanyQuery, PostsByCompanyQueryVariables } from '../../graphql/API';
import { getAgendaUser, postByRoom, postsByCompany } from './queries';
import { useAuthContext } from '../../contexts/AuthContext';
import useNotificationStorageService from '../services/NotificationStorageService';
import { useNotification } from "../../contexts/NotificationProvider";
import { FaUsers } from "react-icons/fa";
import { IoArrowRedoCircleOutline } from 'react-icons/io5';
import SubscriptionComPostWrapper from './subscriptionWrappers/subscriptionComPostWrapper';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spinner } from 'react-bootstrap';
import { IPost } from '../../Types/models';
import Sidebar from '../services/Sidebar/Sidebar';
import { POSTS_PER_PAGE, DISPLAY_NEWS_TYPE } from '../../config';
import { useSearchParams } from 'react-router-dom';
import { IoNewspaper } from "react-icons/io5";
import { MdChat } from "react-icons/md";

const PostScreen = () => {
    // AUTH
    const {userId: AuthUserId} = useAuthContext();
    // I18N
    const { t } = useTranslation();
    const [locale, setLocale] = useState("en");
    // REF
    const lastScrollTopRef = useRef(0);
    // STATE
    const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);
    // NAVIGATION
    const navigate = useNavigate();
    const location = useLocation();
    const deletedId = location.state?.deleted;
    const [searchParams, setSearchParams] = useSearchParams();
    const displayType = searchParams.get('displayType');
    // POSTS
    const [newPosts, setNewPosts] = useState<AgendaPost[]>([]);
    const [posts, setPosts] = useState<AgendaPost[]>([]);
    const [nextToken, setNextToken] = useState('');
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const isRefreshingRef = useRef(false);
    // SUBSCRIPTION
    const [subscriptionKey, setSubscriptionKey] = useState(0);
    // NOTIFICATION
    const notificationContext = useNotification();
    const notificationCount = notificationContext?.notificationCount;
    const setNotificationCount = notificationContext?.setNotificationCount;
    const postCount = notificationContext?.postCount;
    const setPostCount = notificationContext?.setPostCount;
    const {updateNotificationModelCount} = useNotificationStorageService();

    const {data, loading, error, refetch} = 
        useQuery<GetAgendaUserQuery, GetAgendaUserQueryVariables>(getAgendaUser, 
            {variables: {userId: AuthUserId}});
    
    const user = data?.getAgendaUser;
    
    const [fetchData, {data: dataPost, loading: loadingPost, error: errorPost, refetch: refetchPostData, 
        fetchMore: fetchMorePost}] = useLazyQuery<PostByRoomQuery, PostByRoomQueryVariables>(postByRoom);

    useEffect(() => {
        setNotificationCount && setNotificationCount((notificationCount ?? 0) - (postCount ?? 0));
        updateNotificationModelCount('post', 0);
        setPostCount && setPostCount(0);
    }, [location]);

    useEffect(() => {
        if (!user || !user.comId) return;
        if (!user.language) return;
        setLocale(user?.language);
        fetchData({
            variables: {
                room : user?.comId+"_posts",
                sortDirection: ModelSortDirection.DESC, 
                filter: {
                    deleted: {ne: true},
                },
                limit: 8,//POSTS_PER_PAGE,
            }
                // comId: user?.comId, 
                // sortDirection: ModelSortDirection.DESC, 
                // limit: POSTS_PER_PAGE,
                // filter: {
                //     deleted: {eq: false},
                //     room: {eq: user?.comId+"_posts"},
                // }}
                
        },);
        refetchPostData(); // hack to get the refresh (need to modify in the futur POSSIBLY WITH useLocation)
    }, [user]);

    // to reload each time
    useEffect(() => {
        setNotificationCount((notificationCount ?? 0) - (postCount ?? 0));
        setPostCount(0);
    }, []);

    const scrollToBottom = (animate = true) => {
        window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: 'smooth'                       
        });
    };

    // 💡 Iinitial positionning for posts
    useEffect(() => {
        if (displayType != DISPLAY_NEWS_TYPE) {
            if (dataPost && posts.length > 0 && shouldScrollToBottom) {
                const timer = setTimeout(() => {
                    scrollToBottom(false);
                    setShouldScrollToBottom(false);
                }, 500); // 200ms est souvent suffisant pour des images optimisées
            }
        }
    }, [dataPost, posts.length]);

    useEffect(() => {
        // New post on TOP scroll after first load
        if (displayType == DISPLAY_NEWS_TYPE) {
            setPosts((dataPost?.postByRoom?.items as AgendaPost[] || []));
            setNextToken(dataPost?.postByRoom?.nextToken || '');
        // New posts on BOTTOM scroll after first load
        } else {
            if (dataPost?.postByRoom?.items?.length > 0 && posts.length === 0) {
                setPosts((dataPost?.postByRoom?.items as AgendaPost[] || []));
                setNextToken(dataPost?.postByRoom?.nextToken || '');
                setShouldScrollToBottom(true);
            }
        }
    }, [dataPost]);

    // useEffect(() => {
    //     // const timer = setTimeout(() => {
    //     //     setNewPosts([]);
    //     // }, 1000 * 60 * 5); // 5 minutes
        
    //     return () => clearTimeout(timer);
    // }, [newPosts]);

    const loadMore = async () => {
        if (!nextToken || isFetchingMore) {
            return;
        };

        setIsFetchingMore(true);

        const { data: moreData } = await fetchMorePost({
            variables: { nextToken },
            updateQuery: (previousQueryResult: any, { fetchMoreResult }: any) => {
                if (!fetchMoreResult) return previousQueryResult;

                // Met à jour l'état de posts pour y inclure les nouveaux articles
                setPosts(prevPosts => [
                ...prevPosts,
                ...fetchMoreResult.postByRoom.items,
                ]);

                setNextToken(fetchMoreResult.postByRoom.nextToken);

                return {
                    ...previousQueryResult,
                    postByRoom: {
                        ...previousQueryResult.postByRoom,
                        items: [
                            ...previousQueryResult.postByRoom.items,
                            ...fetchMoreResult.postByRoom.items,
                        ],
                        nextToken: fetchMoreResult.postByRoom.nextToken,
                    },
                };
            },
        });
        setIsFetchingMore(false);
    };

    // PAGINATION
    useEffect(() => {
        if (displayType == DISPLAY_NEWS_TYPE) {
            const handleNewsScroll = () => {
                const scrollable = document.documentElement.scrollHeight - window.innerHeight;
                const scrolled = window.scrollY;
                if (scrollable - scrolled < 5) {
                    loadMore();
                }
            };
            window.addEventListener("scroll", handleNewsScroll);
            return () => window.removeEventListener("scroll", handleNewsScroll);
        } else {
            const handleChatScroll = () => {
                const lastScrollTop = lastScrollTopRef.current;
                const currentScrollTop =  document.documentElement.scrollTop;
                const isScrollingUp = currentScrollTop - 5 < lastScrollTop;
                setTimeout( () => { // Important when the page is loading
                    const scrolled = window.scrollY;
                    if (scrolled < 20 && isScrollingUp && nextToken && !isFetchingMore) {
                        loadMore().then(() => {
                            setTimeout( () => { // Important for safari
                                document.documentElement.scrollTop = 10;
                            }, 200);
                        });
                    }
                }, 200);
            };
            window.addEventListener("scroll", handleChatScroll);
            return () => window.removeEventListener("scroll", handleChatScroll);
        }
    }, [nextToken, isFetchingMore]); // loadMore]);

    const refreshingPosts = () => {
        setNewPosts([]);
        refetchPostData();
    };

    // REFRESH BY SCROLLING
    useEffect(() => {
        window.scrollTo(0, 5);

        const handleScrollTop = () => {
            if (window.scrollY < 3 && !isRefreshingRef.current) {
                // console.log('Refreshing posts...');
                isRefreshingRef.current = true;
                refreshingPosts();
                window.scrollTo(0, 5);

                setTimeout(() => {
                    isRefreshingRef.current = false;
                }, 2000);
            }
        };

        window.addEventListener("scroll", handleScrollTop);

        return () => {
            window.removeEventListener("scroll", handleScrollTop);
        };
    }, [loadingPost]);

    const handleSubscriptionError = () => {
        console.warn("Subscription error, retrying in 5s...");
        setTimeout(() => {
          setSubscriptionKey((prev) => prev + 1);
        }, 5000);
    };

    // console.log('test',newPostsData);
    // console.log('test',user);

    const mergedPosts = useMemo(() => {
        // 1. Créer une Map pour suivre les IDs déjà vus
        const uniquePostsMap = new Map<string, AgendaPost>();

        // 2. Ajouter les NOUVEAUX posts (le plus récent est en premier dans newPosts)
        // L'ordre est important : du plus récent au plus ancien
        const allPosts = [...newPosts, ...posts];

        // 3. Parcourir la liste combinée. Le premier post rencontré (le plus récent) est conservé.
        for (const post of allPosts) {
            if (post.room === user?.comId + "_posts" && post.postId) {
                // Si le post n'est pas déjà dans la map, on l'ajoute.
                // Si le post existe, on garde celui qui a été ajouté en premier (ici, de newPosts)
                if (!uniquePostsMap.has(post.postId)) {
                    uniquePostsMap.set(post.postId, post);
                }
            }
        }
        
        // 4. Convertir la Map en tableau. L'ordre de l'itération de la Map sera l'ordre d'insertion
        // (qui est déjà du plus récent au plus ancien dans notre cas)
        return Array.from(uniquePostsMap.values());
        
    }, [newPosts, posts, user?.comId]);

    // Apollo
    if (loadingPost) {
        return (
            <div className="spinnerContainer">
                {/* <Spinner animation="border" role="status" style={styles.spinner as React.CSSProperties}> */}
                <Spinner animation="border" role="status" className="spinner">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>   
            </div>
        )
    };
    // console.log('userId', user?.userId);
    // console.log('comId', user?.comId);
    // if (error) {
    //     return <ApiErrorMessage key="error" title="Error fetching the posts" message={error?.message}/>
    // };

    const isNewPost = (post: AgendaPost) => {
        // console.log(newPosts.some(c => c.postId == post.postId));
        if (deletedId) {
            if (newPosts.some(p => p.postId === deletedId)) {
                const updatedPosts = newPosts.filter(p => p.postId !== deletedId);
                setNewPosts(updatedPosts); // ou autre logique selon ton usage
            }
        }
        const newMessages = newPosts.some(c => c.postId == post.postId && post.room == user.comId + "_posts");

        if (displayType == DISPLAY_NEWS_TYPE) {
            if (newMessages) {
                setTimeout(() => {
                    document.documentElement.scrollTop = 10;
                }, 200);
            }
        } else {
            if (newMessages) {
                setTimeout(() => {
                    scrollToBottom();
                }, 200);
            }
        }
        return newMessages;
    };

    // const mergedPosts = [...newPosts.filter(post => post.room == user?.comId + "_posts"), ...posts.filter(p => !newPosts.some(n => n.postId === p.postId) && p.room === user?.comId + "_posts")];

    // console.log('posts', dataPost?.postsByCompany?.items);
    return (
        <div className="d-flex min-vh-100">
            <Sidebar user={user} />
            <div className="commentScreenContainer flex-grow-1 p-3">
                {loadingPost && (
                    <div style={{ textAlign: 'center', marginBottom: 10 }}>
                        <Spinner animation="border" size="sm" />
                        <span> {t('Refreshing...')}</span>
                    </div>
                )}
                <div className="tabs">
                    {/* <Text style={[styles.textTab, styles.selectedTab]} >Private Room</Text> */}
                    <div className="post_row">
                        <Link to={t('link.users')} className='linkPostTeammate'>
                            <FaUsers size={30} color="#CCC" />
                            <span className='textTab'>{t('worker.coworkers')}</span>
                            <IoArrowRedoCircleOutline onClick={() => ""} className="hover-icon" size={30}/>
                            {/* <Entypo name="arrow-with-circle-right" style={styles.arrow} size={30} /> */}
                        </Link>
                    </div>
                    { displayType == DISPLAY_NEWS_TYPE ?
                        <a href="/posts">
                            <MdChat size={60} className="post-display-type-icon"/>
                        </a>
                    :
                        <a href="/posts?displayType=news">
                            <IoNewspaper size={60} className="post-display-type-icon"/>
                        </a>
                    }
                </div>
                {user?.comId && (
                    <SubscriptionComPostWrapper
                        key={subscriptionKey}
                        comId={user.comId}
                        subscriptionKey={subscriptionKey}
                        onNewPost={(post) => {
                            if (post.room == user.comId + "_posts") {
                                setNewPosts((prev) => [post, ...prev]);
                            }
                        }}
                        onError={handleSubscriptionError}
                    />
                )}

                { displayType == DISPLAY_NEWS_TYPE ? (
                    mergedPosts.length === 0 && !loadingPost ? (
                        // Affiche "Veuillez écrire un commentaire"
                        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                            <p>{t('post.pleaseWriteComment')}</p>
                        </div>
                    ) : (
                        mergedPosts.map((item, index) =>
                            item ? (
                                <Post
                                    key={item.postId || index}
                                    post={{
                                        ...item,
                                        user: (item as any)?.user || null,
                                    } as IPost}
                                    includedDetails={true}
                                    isNew={isNewPost(item)}
                                    locale={locale}
                                />
                            ) : null
                        )
                    )
                ) : (
                    // <div // FOR NEW POSTS ON BOTTOM
                    mergedPosts.length === 0 && !loadingPost ? (
                        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                            <p>{t('post.pleaseWriteComment')}</p>
                        </div>
                    ) : (
                        [...mergedPosts].reverse() // reverse to have the latest at the bottom (on mergedPosts copy)
                            .map((item, index) =>
                            item ? (
                                <Post
                                    key={item.postId || index}
                                    post={{
                                        ...item,
                                        user: (item as any)?.user || null,
                                    } as IPost}
                                    // Liked={true}
                                    includedDetails={true}
                                    isNew={isNewPost(item)}
                                    locale={locale}
                                />
                            ) : null
                        )
                    )
                )}
                { user && user.comId &&
                    <Input user={{...user,
                        shifts: (user as any)?.shifts || null,
                        shiftApps: (user as any)?.shiftApps || null,
                        posts: (user as any)?.posts || null,
                        docs: (user as any)?.docs || null,
                        requests: (user as any)?.requests || null,
                        punches: (user as any)?.punches || null,
                        honors: (user as any)?.honors || null                
                    }} comId={user?.comId} locale={locale} room={user?.comId+"_posts"}/>
                }
            </div>
        </div>
    );
};

export default PostScreen;
